import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, IconButton, TextField } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

// Custom Time Picker Component with Calendar
const CustomTimePicker = ({ open, onClose, onSelect, initialValue }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState(12);
  const [selectedMinute, setSelectedMinute] = useState(0);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  const hourScrollRef = useRef(null);
  const minuteScrollRef = useRef(null);

  useEffect(() => {
    if (open && initialValue) {
      const date = new Date(initialValue);
      setSelectedDate(date);
      setSelectedHour(date.getHours());
      setSelectedMinute(date.getMinutes());
      setCurrentMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    } else if (open) {
      const now = new Date();
      setSelectedDate(now);
      setSelectedHour(now.getHours());
      setSelectedMinute(now.getMinutes());
      setCurrentMonth(new Date(now.getFullYear(), now.getMonth(), 1));
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (hourScrollRef.current) {
          const hourItem = hourScrollRef.current.querySelector(`[data-value="${selectedHour}"]`);
          if (hourItem) {
            hourItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
        if (minuteScrollRef.current) {
          const minuteItem = minuteScrollRef.current.querySelector(`[data-value="${selectedMinute}"]`);
          if (minuteItem) {
            minuteItem.scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        }
      }, 100);
    }
  }, [open, selectedHour, selectedMinute]);

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek };
  };

  const handlePreviousMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const handleDateSelect = (day) => {
    const newDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    setSelectedDate(newDate);
  };

  const isDateSelected = (day) => {
    return selectedDate.getDate() === day &&
           selectedDate.getMonth() === currentMonth.getMonth() &&
           selectedDate.getFullYear() === currentMonth.getFullYear();
  };

  const isToday = (day) => {
    const today = new Date();
    return today.getDate() === day &&
           today.getMonth() === currentMonth.getMonth() &&
           today.getFullYear() === currentMonth.getFullYear();
  };

  const formatDateForInput = (date) => {
    const d = new Date(date);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleDateInputChange = (e) => {
    const value = e.target.value;
    const dateRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = value.match(dateRegex);
    
    if (match) {
      const [, day, month, year] = match;
      const newDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
      if (!isNaN(newDate.getTime())) {
        setSelectedDate(newDate);
        setCurrentMonth(new Date(newDate.getFullYear(), newDate.getMonth(), 1));
      }
    }
  };

  const handleConfirm = () => {
    const finalDate = new Date(selectedDate);
    finalDate.setHours(selectedHour);
    finalDate.setMinutes(selectedMinute);
    finalDate.setSeconds(0);
    finalDate.setMilliseconds(0);
    onSelect(finalDate);
    onClose();
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);
  const minutes = Array.from({ length: 60 }, (_, i) => i);
  const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentMonth);
  const weekDays = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
  const monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 
                      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
        <Typography variant="h6">Sélectionner la date et l'heure</Typography>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {/* Text Input for Manual Date Entry */}
        <Box sx={{ mb: 3 }}>
          <TextField
            type="text"
            fullWidth
            value={formatDateForInput(selectedDate)}
            onChange={handleDateInputChange}
            label="Date"
            placeholder="DD/MM/YYYY"
            helperText="Format: DD/MM/YYYY"
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        {/* Calendar */}
        <Box sx={{ mb: 3, border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
          {/* Month Navigation */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <IconButton onClick={handlePreviousMonth} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
            </Typography>
            <IconButton onClick={handleNextMonth} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Week Days Header */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5, mb: 1 }}>
            {weekDays.map((day) => (
              <Box
                key={day}
                sx={{
                  textAlign: 'center',
                  py: 0.5,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: 'text.secondary'
                }}
              >
                {day}
              </Box>
            ))}
          </Box>

          {/* Calendar Days */}
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {/* Empty cells for days before month starts */}
            {Array.from({ length: startingDayOfWeek }, (_, i) => (
              <Box key={`empty-${i}`} sx={{ aspectRatio: '1', p: 0.5 }} />
            ))}
            
            {/* Days of the month */}
            {Array.from({ length: daysInMonth }, (_, i) => {
              const day = i + 1;
              const selected = isDateSelected(day);
              const today = isToday(day);
              
              return (
                <Box
                  key={day}
                  onClick={() => handleDateSelect(day)}
                  sx={{
                    aspectRatio: '1',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: 1,
                    fontSize: '0.875rem',
                    backgroundColor: selected ? 'primary.main' : today ? 'action.hover' : 'transparent',
                    color: selected ? 'white' : 'text.primary',
                    fontWeight: selected || today ? 600 : 400,
                    border: today && !selected ? '1px solid' : 'none',
                    borderColor: 'primary.main',
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: selected ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {day}
                </Box>
              );
            })}
          </Box>
        </Box>
        
        {/* Time Selection */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'center' }}>
          {/* Hours Picker */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              Heures
            </Typography>
            <Box
              ref={hourScrollRef}
              sx={{
                height: 200,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {hours.map((hour) => (
                <Box
                  key={hour}
                  data-value={hour}
                  onClick={() => setSelectedHour(hour)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    backgroundColor: selectedHour === hour ? 'primary.main' : 'transparent',
                    color: selectedHour === hour ? 'white' : 'text.primary',
                    fontWeight: selectedHour === hour ? 600 : 400,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: selectedHour === hour ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {hour.toString().padStart(2, '0')}
                </Box>
              ))}
            </Box>
          </Box>

          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
            :
          </Typography>

          {/* Minutes Picker */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ mb: 1, display: 'block', fontWeight: 600 }}>
              Minutes
            </Typography>
            <Box
              ref={minuteScrollRef}
              sx={{
                height: 200,
                overflowY: 'auto',
                border: '1px solid #e0e0e0',
                borderRadius: 1,
                position: 'relative',
                '&::-webkit-scrollbar': {
                  width: '6px',
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  borderRadius: '3px',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '3px',
                  '&:hover': {
                    background: '#555',
                  },
                },
              }}
            >
              {minutes.map((minute) => (
                <Box
                  key={minute}
                  data-value={minute}
                  onClick={() => setSelectedMinute(minute)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    cursor: 'pointer',
                    backgroundColor: selectedMinute === minute ? 'primary.main' : 'transparent',
                    color: selectedMinute === minute ? 'white' : 'text.primary',
                    fontWeight: selectedMinute === minute ? 600 : 400,
                    transition: 'all 0.2s',
                    '&:hover': {
                      backgroundColor: selectedMinute === minute ? 'primary.dark' : 'action.hover',
                    },
                  }}
                >
                  {minute.toString().padStart(2, '0')}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        {/* Selected DateTime Display */}
        <Box sx={{ mt: 3, p: 2, backgroundColor: 'action.hover', borderRadius: 1, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Sélection actuelle:
          </Typography>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            {selectedDate.toLocaleDateString('fr-FR')} à {selectedHour.toString().padStart(2, '0')}:{selectedMinute.toString().padStart(2, '0')}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} variant="outlined">
          Annuler
        </Button>
        <Button onClick={handleConfirm} variant="contained">
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomTimePicker;